// frontend/src/hooks/usePOData.js
// Reusable hooks for purchase order reporting data.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiJson, readResult } from '../queries/api';
import { ApiError, apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from '../contexts/AuthContext';
import {
  homeTables as homeTablesKey,
  purchaseOrders as purchaseOrdersKey,
  poReceiptHistory as poReceiptHistoryKey,
  poPlanningSummary as poPlanningSummaryKey,
} from '../queries/keys';

const THIRTY_SEC = 30 * 1000;
const FIVE_MIN = 5 * 60 * 1000;

/**
 * Fetch outstanding POs for the home-page dashboard table.
 * Returns PO headers with outstanding (not fully received) lines.
 *
 * Caching: 30s staleTime + tab-back refresh. Matches the quick-glance
 * summary profile used by KPIs and useShortageItems on the home page.
 */
export function useOutstandingPOs() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: homeTablesKey.outstandingPOs(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/pos/outstanding/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Single PO header, with order_lines + nested supplier/location/currency:
 * the full PO record for any view that needs it.
 *
 * `enabled: !!storeId && !!id`: without an id there is no PO to ask for,
 * and `/.../undefined/` would be a 404.
 *
 * A server answer (404 for a deleted PO or bad URL, 5xx) surfaces at once;
 * only a request that never reached the server is retried, once
 * (queries/client.js `retryQueryOnce`).
 */
export function usePurchaseOrder(id) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: purchaseOrdersKey.detail(storeId, id),
    queryFn: () => apiJson.get(`/api/po-calculations/purchase-orders/${id}/`),
    staleTime: THIRTY_SEC,
    enabled: !!storeId && !!id,
  });
  return {
    ...readResult(query, t),
    // Typed access to the underlying `ApiError` so callers can branch on
    // `.status` / `.code` / `.body` (e.g. 404 → close the tab). The
    // `error` from readResult is the user-facing translated string.
    errorObject: query.error,
  };
}

/**
 * The delivery date issuing this draft as it stands would set (GET
 * <id>/issue-date/) — what a draft's page shows as its expected delivery,
 * since a draft stores none. `enabled` only for a draft Manexys owns: the
 * endpoint refuses an issued PO and an ERP-owned one.
 */
export function useIssueDate(id, { enabled }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: purchaseOrdersKey.issueDate(storeId, id),
    queryFn: () => apiJson.get(`/api/po-calculations/purchase-orders/${id}/issue-date/`),
    staleTime: THIRTY_SEC,
    enabled: enabled && !!storeId && !!id,
  });
  return readResult(query, t, 'purchaseOrders.messages.issueDateFailed');
}

/**
 * Receipt history for a single purchase order — the array of goods-receipt
 * documents tied to a PO header. Used by both the PO list's detail expand
 * (PurchaseOrders.jsx) and the PO details page (PurchaseOrderDetail.jsx);
 * shared cache so opening the details after expanding the row hits warm data.
 *
 * `enabled: !!poId` so a row that never expanded never fires a request.
 */
export function useReceiptHistory(poId) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: poReceiptHistoryKey.byPo(storeId, poId),
    queryFn: () => apiJson.get(
      `/api/po-calculations/purchase-orders/${poId}/receipt-history/`,
    ),
    staleTime: FIVE_MIN,
    enabled: !!storeId && !!poId,
  });
  return readResult(query, t);
}

/**
 * PO recommendations planning summary — the attention banner at the top of the
 * Purchase Recommendations page: the items held out of planning right now and
 * the counts of recommendations that need a look.
 *
 * Always stale (staleTime 0), so it is re-read whenever its tab or the browser
 * tab comes back on screen (tabs/TabsHost): it reports present state, and the
 * writes that move it — a supplier assigned on the Supplier catalog, a count
 * entered on Inventory, a Data health fix — happen on other screens that do
 * not each name this cache.
 */
export function usePOPlanningSummary() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: poPlanningSummaryKey.all(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/po-calculations/recommendations/planning-summary/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: 0,
    enabled: !!storeId,
  });
  return readResult(query, t);
}
